import { Sparkles, Loader2, type LucideIcon } from 'lucide-react';
import { Button } from '@marine/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@marine/ui/tooltip';
import { useCan } from '@/modules/tenant/hooks/useTenantAuth';

interface AiActionButtonProps {
  label: string;
  loadingLabel: string;
  variant?: 'inline' | 'prominent';
  isLoading?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
  /**
   * Phase 43-06: optional override for the leading icon (defaults to Sparkles).
   * Used by CopilotActions to surface a RefreshCw icon on the
   * "Re-run scorecard" + "Regenerate description" buttons. Backwards-
   * compatible — every existing call site keeps the Sparkles default.
   */
  icon?: LucideIcon;
}

export function AiActionButton({
  label,
  loadingLabel,
  variant = 'inline',
  isLoading = false,
  disabled = false,
  disabledReason,
  onClick,
  icon,
}: AiActionButtonProps) {
  // ACL-09 (#611, D-22) — every paid AI trigger is hidden, not disabled, for an
  // employee without inventory.ai.generate; the server 403 is the backstop.
  const canGenerate = useCan('inventory.ai.generate');
  if (!canGenerate) {
    return null;
  }

  const buttonVariant = variant === 'inline' ? 'ghost' : 'outline';
  const Icon = isLoading ? Loader2 : (icon ?? Sparkles);
  const displayLabel = isLoading ? loadingLabel : label;
  const isDisabled = disabled || isLoading;

  const button = (
    <Button
      variant={buttonVariant}
      size="sm"
      disabled={isDisabled}
      onClick={onClick}
      className={disabled && !isLoading ? 'opacity-50 cursor-not-allowed' : undefined}
    >
      <Icon
        className={`h-4 w-4 text-violet-600 dark:text-violet-400 ${isLoading ? 'animate-spin' : ''}`}
        role="img"
      />
      {(variant === 'prominent' || isLoading) && (
        <span>{displayLabel}</span>
      )}
      {variant === 'inline' && !isLoading && (
        <span>{displayLabel}</span>
      )}
    </Button>
  );

  if (disabled && disabledReason && !isLoading) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{button}</span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    );
  }

  return button;
}
